import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Save, Trash2, X, Eye, FileText, LayoutGrid, GripVertical, Calendar as CalendarIcon, Settings as SettingsIcon, Users } from "lucide-react";
import Topbar from "@/components/layout/Topbar";
import Widget from "@/components/widgets/Widget";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/context/AuthContext";

const CATEGORY_LABEL = {
  kpi: "KPI cards",
  chart: "Charts",
  table: "Tables",
  advanced: "Advanced",
};

export default function DashboardBuilder() {
  const { dashboardId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [catalog, setCatalog] = useState([]);
  const [draft, setDraft] = useState({
    name: "",
    description: "",
    widgets: [],
    is_report: false,
    output_formats: ["pdf"],
    email_recipients: [],
    schedule: null,
    shared_with: [],
  });
  const [users, setUsers] = useState([]);
  const [previewing, setPreviewing] = useState(null); // instance_id of widget being previewed
  const [previewData, setPreviewData] = useState({});
  const [busy, setBusy] = useState(false);

  // Load catalog + dashboard (if editing)
  useEffect(() => {
    api.get("/widgets/catalog").then((r) => setCatalog(r.data));
    if (user?.role === "SUPER_ADMIN" || user?.role === "EXEC") {
      api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    }
    if (dashboardId) {
      api
        .get(`/my-dashboards/${dashboardId}`)
        .then((r) => setDraft({ ...r.data }))
        .catch((e) => toast.error(formatApiError(e)));
    }
  }, [dashboardId, user?.role]);

  const groupedCatalog = useMemo(() => {
    const out = { kpi: [], chart: [], table: [], advanced: [] };
    catalog.forEach((w) => out[w.category]?.push(w));
    return out;
  }, [catalog]);

  const addWidget = (cat) => {
    const inst = {
      instance_id: `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      widget_type: cat.id,
      title: cat.name,
      w: cat.default_w,
      h: cat.default_h,
      filters: { months: 12 },
      options: {},
    };
    setDraft((d) => ({ ...d, widgets: [...d.widgets, inst] }));
  };

  const removeWidget = (instance_id) => {
    setDraft((d) => ({
      ...d,
      widgets: d.widgets.filter((w) => w.instance_id !== instance_id),
    }));
  };

  const updateWidget = (instance_id, patch) => {
    setDraft((d) => ({
      ...d,
      widgets: d.widgets.map((w) => (w.instance_id === instance_id ? { ...w, ...patch } : w)),
    }));
  };

  const moveWidget = (instance_id, dir) => {
    setDraft((d) => {
      const idx = d.widgets.findIndex((w) => w.instance_id === instance_id);
      if (idx < 0) return d;
      const target = dir === "up" ? idx - 1 : idx + 1;
      if (target < 0 || target >= d.widgets.length) return d;
      const next = [...d.widgets];
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...d, widgets: next };
    });
  };

  const previewWidget = async (w) => {
    setPreviewing(w.instance_id);
    try {
      const { data } = await api.post("/widgets/data", {
        widget_type: w.widget_type,
        filters: w.filters || {},
      });
      setPreviewData((s) => ({ ...s, [w.instance_id]: data }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPreviewing(null);
    }
  };

  const save = async () => {
    if (!draft.name.trim()) {
      toast.error("Give it a name");
      return;
    }
    if (draft.widgets.length === 0) {
      toast.error("Add at least one widget");
      return;
    }
    setBusy(true);
    try {
      const body = { ...draft };
      if (dashboardId) {
        await api.patch(`/my-dashboards/${dashboardId}`, body);
        toast.success("Saved");
      } else {
        const { data } = await api.post("/my-dashboards", body);
        toast.success("Created");
        navigate(`/dashboards/builder/${data.dashboard_id}`, { replace: true });
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const canShare = user?.role === "SUPER_ADMIN" || user?.role === "EXEC";

  return (
    <>
      <Toaster position="top-right" richColors />
      <Topbar
        title={dashboardId ? "Edit Dashboard" : "New Dashboard"}
        subtitle="Compose widgets, set filters, save"
        right={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/my-dashboards")}
              className="h-9 rounded-sm border border-slate-200 px-3 text-xs font-semibold uppercase tracking-widest text-slate-600 hover:border-navy-600"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={busy}
              data-testid="save-dashboard"
              className="flex h-9 items-center gap-1.5 rounded-sm bg-brand-orange px-3 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#C1701C] disabled:opacity-60"
            >
              <Save className="h-3.5 w-3.5" />
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-12 gap-4 p-6 animate-fade-in" data-testid="dashboard-builder">
        {/* Left: widget palette */}
        <aside className="col-span-12 lg:col-span-3">
          <div className="sticky top-6 space-y-4">
            <div className="rounded-md border border-slate-200 bg-white p-4">
              <h3 className="font-display text-sm font-semibold text-navy-600">Widget catalog</h3>
              <p className="mt-1 text-xs text-slate-500">Click a widget to add it.</p>
              <div className="mt-3 space-y-3">
                {Object.entries(groupedCatalog).map(([cat, items]) => (
                  <div key={cat}>
                    <div className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-brand-orange">
                      {CATEGORY_LABEL[cat]} ({items.length})
                    </div>
                    <div className="space-y-1">
                      {items.map((w) => (
                        <button
                          key={w.id}
                          type="button"
                          onClick={() => addWidget(w)}
                          data-testid={`palette-${w.id}`}
                          className="flex w-full items-center justify-between gap-2 rounded-sm border border-slate-200 px-2 py-1.5 text-left text-xs text-slate-600 transition-colors hover:border-brand-orange hover:bg-orange-50 hover:text-brand-orange"
                        >
                          <span className="truncate">{w.name}</span>
                          <Plus className="h-3 w-3 shrink-0 text-slate-400" />
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </aside>

        {/* Right: canvas + settings */}
        <main className="col-span-12 space-y-4 lg:col-span-9">
          {/* Meta */}
          <div className="rounded-md border border-slate-200 bg-white p-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Name</label>
                <input
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  data-testid="dash-name"
                  placeholder="Q1 Executive Briefing"
                  className="mt-1.5 block h-10 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm outline-none focus:border-navy-600"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Description</label>
                <input
                  value={draft.description || ""}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  data-testid="dash-description"
                  placeholder="Optional one-liner"
                  className="mt-1.5 block h-10 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm outline-none focus:border-navy-600"
                />
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-3">
              <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={!!draft.is_report}
                  onChange={(e) => setDraft({ ...draft, is_report: e.target.checked })}
                  data-testid="dash-is-report"
                  className="h-4 w-4 accent-brand-orange"
                />
                <FileText className="h-3.5 w-3.5 text-brand-orange" />
                Save as Report
              </label>
              {!draft.is_report ? null : (
                <>
                  <span className="text-[10px] uppercase tracking-widest text-slate-500">Formats:</span>
                  {["pdf", "xlsx", "csv"].map((fmt) => (
                    <label key={fmt} className="flex cursor-pointer items-center gap-1.5 text-xs text-slate-600">
                      <input
                        type="checkbox"
                        checked={draft.output_formats?.includes(fmt) || false}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...(draft.output_formats || []), fmt]
                            : (draft.output_formats || []).filter((f) => f !== fmt);
                          setDraft({ ...draft, output_formats: next });
                        }}
                        className="h-3.5 w-3.5 accent-brand-orange"
                      />
                      {fmt.toUpperCase()}
                    </label>
                  ))}
                </>
              )}
            </div>

            {draft.is_report && <ReportExtras draft={draft} setDraft={setDraft} />}

            {canShare && <SharePanel draft={draft} setDraft={setDraft} users={users} />}
          </div>

          {/* Widget canvas */}
          {draft.widgets.length === 0 ? (
            <div
              data-testid="builder-empty"
              className="rounded-md border-2 border-dashed border-slate-200 bg-white p-12 text-center"
            >
              <LayoutGrid className="mx-auto h-10 w-10 text-slate-300" />
              <p className="mt-3 text-sm text-slate-500">
                Click widgets in the catalog on the left to add them here.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {draft.widgets.map((w, i) => (
                <WidgetEditor
                  key={w.instance_id}
                  widget={w}
                  index={i}
                  total={draft.widgets.length}
                  onChange={(patch) => updateWidget(w.instance_id, patch)}
                  onRemove={() => removeWidget(w.instance_id)}
                  onMove={(dir) => moveWidget(w.instance_id, dir)}
                  onPreview={() => previewWidget(w)}
                  preview={previewData[w.instance_id]}
                  previewing={previewing === w.instance_id}
                />
              ))}
            </div>
          )}
        </main>
      </div>
    </>
  );
}

function WidgetEditor({ widget, index, total, onChange, onRemove, onMove, onPreview, preview, previewing }) {
  const [open, setOpen] = useState(false);
  const [pvDivs, setPvDivs] = useState(null);
  const isPv = widget.widget_type?.startsWith("pv_");

  // Load divisions on demand when the user opens filters on a PV widget.
  useEffect(() => {
    if (isPv && open && pvDivs === null) {
      api
        .get("/widgets/price-volume/divisions")
        .then((r) => setPvDivs(r.data || []))
        .catch(() => setPvDivs([]));
    }
  }, [isPv, open, pvDivs]);

  const selectedDiv = (pvDivs || []).find((d) => d.division_id === widget.filters?.division_id);
  return (
    <div
      data-testid={`builder-widget-${widget.instance_id}`}
      className="rounded-md border border-slate-200 bg-white"
    >
      <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex flex-col gap-0.5">
            <button
              type="button"
              onClick={() => onMove("up")}
              disabled={index === 0}
              className="rounded-sm p-0.5 text-slate-400 hover:bg-slate-100 hover:text-navy-600 disabled:opacity-30"
              data-testid={`move-up-${widget.instance_id}`}
            >
              <GripVertical className="h-3 w-3 -rotate-90" />
            </button>
            <button
              type="button"
              onClick={() => onMove("down")}
              disabled={index === total - 1}
              className="rounded-sm p-0.5 text-slate-400 hover:bg-slate-100 hover:text-navy-600 disabled:opacity-30"
              data-testid={`move-down-${widget.instance_id}`}
            >
              <GripVertical className="h-3 w-3 rotate-90" />
            </button>
          </div>
          <div>
            <input
              value={widget.title || ""}
              onChange={(e) => onChange({ title: e.target.value })}
              data-testid={`widget-title-${widget.instance_id}`}
              className="block h-8 w-72 rounded-sm border border-transparent bg-transparent px-1 text-sm font-semibold text-navy-600 outline-none hover:border-slate-200 focus:border-navy-600"
              placeholder="Widget title"
            />
            <div className="px-1 text-[10px] uppercase tracking-widest text-slate-500">
              {widget.widget_type}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[10px] uppercase tracking-widest text-slate-500">Width</span>
          <select
            value={widget.w}
            onChange={(e) => onChange({ w: Number(e.target.value) })}
            data-testid={`widget-w-${widget.instance_id}`}
            className="h-8 rounded-sm border border-slate-200 bg-white px-2 text-xs"
          >
            {[3, 4, 6, 8, 12].map((n) => (
              <option key={n} value={n}>
                {n}/12
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex h-8 items-center gap-1 rounded-sm border border-slate-200 px-2 text-xs text-slate-600 hover:border-navy-600"
          >
            <SettingsIcon className="h-3 w-3" />
            Filters
          </button>
          <button
            type="button"
            onClick={onPreview}
            data-testid={`preview-${widget.instance_id}`}
            disabled={previewing}
            className="flex h-8 items-center gap-1 rounded-sm border border-slate-200 px-2 text-xs text-slate-600 hover:border-navy-600 disabled:opacity-50"
          >
            <Eye className="h-3 w-3" />
            Preview
          </button>
          <button
            type="button"
            onClick={onRemove}
            data-testid={`remove-widget-${widget.instance_id}`}
            className="rounded-sm p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {open && (
        <div className="grid grid-cols-1 gap-3 border-b border-slate-100 px-4 py-3 sm:grid-cols-3" data-testid={`filters-${widget.instance_id}`}>
          {isPv ? (
            <>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Division</label>
                <select
                  value={widget.filters?.division_id || ""}
                  onChange={(e) => onChange({ filters: { ...widget.filters, division_id: e.target.value || undefined, customer_id: undefined } })}
                  data-testid={`pv-division-${widget.instance_id}`}
                  className="mt-1 block h-9 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
                >
                  <option value="">Auto (first available)</option>
                  {(pvDivs || []).map((d) => (
                    <option key={d.division_id} value={d.division_id}>
                      {d.division_name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Anchor month (optional)</label>
                <input
                  type="month"
                  value={widget.filters?.anchor_month || ""}
                  onChange={(e) => onChange({ filters: { ...widget.filters, anchor_month: e.target.value || undefined } })}
                  data-testid={`pv-anchor-${widget.instance_id}`}
                  className="mt-1 block h-9 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Customer drill-down</label>
                <select
                  value={widget.filters?.customer_id || ""}
                  onChange={(e) => onChange({ filters: { ...widget.filters, customer_id: e.target.value || undefined } })}
                  data-testid={`pv-customer-${widget.instance_id}`}
                  className="mt-1 block h-9 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
                  disabled={!selectedDiv}
                >
                  <option value="">All customers</option>
                  {(selectedDiv?.customers || []).map((c) => (
                    <option key={c.customer_id} value={c.customer_id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Months</label>
                <input
                  type="number"
                  min={1}
                  max={36}
                  value={widget.filters?.months ?? 12}
                  onChange={(e) => onChange({ filters: { ...widget.filters, months: Number(e.target.value), start_date: undefined, end_date: undefined } })}
                  className="mt-1 block h-9 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
                />
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Source ERP</label>
                <select
                  value={widget.filters?.source_erp || ""}
                  onChange={(e) => onChange({ filters: { ...widget.filters, source_erp: e.target.value || undefined } })}
                  className="mt-1 block h-9 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
                >
                  <option value="">Any ERP</option>
                  {["Chempax", "Syspro", "Infor", "Sage", "QuickBooks", "Misys", "Smartsheet"].map((e) => (
                    <option key={e}>{e}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Top N (tables / top-companies)</label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={widget.filters?.top_n ?? 10}
                  onChange={(e) => onChange({ filters: { ...widget.filters, top_n: Number(e.target.value) } })}
                  className="mt-1 block h-9 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
                />
              </div>
            </>
          )}
        </div>
      )}

      {preview && (
        <div className="border-t border-slate-100 p-3" style={{ minHeight: 240 }} data-testid={`preview-output-${widget.instance_id}`}>
          <Widget widget={preview} />
        </div>
      )}
    </div>
  );
}

function ReportExtras({ draft, setDraft }) {
  const [emailDraft, setEmailDraft] = useState("");
  const sched = draft.schedule || null;
  const setSchedField = (patch) =>
    setDraft({ ...draft, schedule: { ...(sched || { frequency: "weekly", hour: 8, minute: 0, enabled: true, day_of_week: 1 }), ...patch } });
  const removeRecipient = (e) => setDraft({ ...draft, email_recipients: (draft.email_recipients || []).filter((x) => x !== e) });
  const addRecipient = () => {
    if (!emailDraft.trim()) return;
    if ((draft.email_recipients || []).includes(emailDraft.trim())) return;
    setDraft({ ...draft, email_recipients: [...(draft.email_recipients || []), emailDraft.trim()] });
    setEmailDraft("");
  };

  return (
    <div className="mt-3 space-y-3 rounded-sm bg-slate-50 p-3" data-testid="report-extras">
      <div className="flex items-center gap-2">
        <CalendarIcon className="h-3.5 w-3.5 text-brand-orange" />
        <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
          Delivery schedule
        </span>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
        <select
          value={sched?.frequency || ""}
          onChange={(e) => setSchedField({ frequency: e.target.value })}
          data-testid="sched-frequency"
          className="h-9 rounded-sm border border-slate-200 bg-white px-2 text-xs"
        >
          <option value="">No schedule</option>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
        </select>
        {sched?.frequency === "weekly" && (
          <select
            value={sched.day_of_week ?? 1}
            onChange={(e) => setSchedField({ day_of_week: Number(e.target.value) })}
            data-testid="sched-dow"
            className="h-9 rounded-sm border border-slate-200 bg-white px-2 text-xs"
          >
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d, i) => (
              <option key={d} value={i}>
                {d}
              </option>
            ))}
          </select>
        )}
        {sched?.frequency === "monthly" && (
          <input
            type="number"
            min={1}
            max={31}
            value={sched.day_of_month ?? 1}
            onChange={(e) => setSchedField({ day_of_month: Number(e.target.value) })}
            data-testid="sched-dom"
            className="h-9 rounded-sm border border-slate-200 bg-white px-2 text-xs"
            placeholder="Day of month"
          />
        )}
        {sched && (
          <input
            type="time"
            value={`${String(sched.hour ?? 8).padStart(2, "0")}:${String(sched.minute ?? 0).padStart(2, "0")}`}
            onChange={(e) => {
              const [h, m] = e.target.value.split(":").map(Number);
              setSchedField({ hour: h, minute: m });
            }}
            data-testid="sched-time"
            className="h-9 rounded-sm border border-slate-200 bg-white px-2 text-xs"
          />
        )}
      </div>

      <div>
        <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
          Email recipients (display-only — no SMTP integration)
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {(draft.email_recipients || []).map((e) => (
            <span
              key={e}
              className="inline-flex items-center gap-1 rounded-sm bg-navy-600 px-2 py-0.5 text-[10px] font-medium text-white"
            >
              {e}
              <button onClick={() => removeRecipient(e)} className="text-white/70 hover:text-white">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <input
            value={emailDraft}
            onChange={(e) => setEmailDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addRecipient())}
            data-testid="email-recipient-input"
            placeholder="vc@example.com — press Enter"
            className="h-7 flex-1 min-w-[200px] rounded-sm border border-slate-200 bg-white px-2 text-xs outline-none focus:border-navy-600"
          />
          <button
            type="button"
            onClick={addRecipient}
            data-testid="add-recipient"
            className="h-7 rounded-sm bg-brand-orange px-2 text-[10px] font-semibold uppercase tracking-widest text-white hover:bg-[#C1701C]"
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
}

function SharePanel({ draft, setDraft, users }) {
  const toggle = (uid) => {
    const cur = draft.shared_with || [];
    setDraft({
      ...draft,
      shared_with: cur.includes(uid) ? cur.filter((x) => x !== uid) : [...cur, uid],
    });
  };
  return (
    <div className="mt-3 rounded-sm bg-slate-50 p-3" data-testid="share-panel">
      <div className="flex items-center gap-2">
        <Users className="h-3.5 w-3.5 text-brand-orange" />
        <span className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
          Share with users (data is always re-scoped to viewer's permissions)
        </span>
      </div>
      <div className="mt-2 grid max-h-32 grid-cols-2 gap-1 overflow-y-auto sm:grid-cols-3">
        {users.map((u) => {
          const on = (draft.shared_with || []).includes(u.user_id);
          return (
            <button
              key={u.user_id}
              type="button"
              onClick={() => toggle(u.user_id)}
              data-testid={`share-${u.user_id}`}
              className={`truncate rounded-sm border px-2 py-1.5 text-left text-xs transition-colors ${
                on
                  ? "border-brand-orange bg-brand-orange/10 text-brand-orange"
                  : "border-slate-200 text-slate-500 hover:border-navy-600"
              }`}
            >
              {u.name} <span className="text-[10px] text-slate-400">{u.role}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
